import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { TrendingUp, Loader2, Gift } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import AndroidDownload from "@/components/AndroidDownload";
import ForgotPassword from "@/components/ForgotPassword";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [referral, setReferral] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState("");
  const [otp, setOtp] = useState("");
  const [verificationToken, setVerificationToken] = useState("");
  const [notice, setNotice] = useState("");
  const [cooldown, setCooldown] = useState(0);
  const [forgot, setForgot] = useState(false);
  const [resetSuccess, setResetSuccess] = useState("");
  useEffect(() => {
    if (!cooldown) return;
    const timer = setTimeout(() => setCooldown((value) => Math.max(0, value - 1)), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const sendOtp = async () => {
    const { data } = await api.post("/auth/email-otp", { email });
    setChallenge(data.challenge_id); setOtp(""); setVerificationToken("");
    setNotice("OTP sent. Enter the six-digit code from your email. It expires in 10 minutes.");
    setCooldown(data.resend_after || 60);
  };

  useEffect(() => {
    const ref = new URLSearchParams(window.location.search).get("ref");
    if (ref) {
      setReferral(ref.toUpperCase());
      setMode("register");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      if (mode === "login") await login(email, password);
      else {
        if (!challenge) { await sendOtp(); return; }
        let proof = verificationToken;
        if (!proof) {
          const { data } = await api.post("/auth/email-otp/verify", { challenge_id: challenge, code: otp });
          proof = data.verification_token; setVerificationToken(proof);
        }
        await register(name, email, password, referral.trim().toUpperCase(), proof);
      }
      navigate("/");
    } catch (err) {
      setError(err.response ? formatApiError(err.response.data?.detail) : "Unable to sign in and save your session. Check your connection and try again.");
    } finally {
      setBusy(false);
    }
  };

  if (forgot) return <ForgotPassword initialEmail={email} onBack={() => setForgot(false)} onComplete={(value) => {
    setEmail(value); setPassword(""); setMode("login"); setForgot(false); setError(""); setResetSuccess("Password updated. Sign in with your new password.");
  }} />;

  return (
    <div className="min-h-screen grain flex flex-col justify-center px-6 relative overflow-hidden">
      <div className="absolute -top-32 -right-24 w-80 h-80 rounded-full bg-[#00E676]/10 blur-3xl" />
      <div className="absolute -bottom-32 -left-24 w-80 h-80 rounded-full bg-[#3B82F6]/10 blur-3xl" />

      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md mx-auto"
      >
        <div className="flex items-center gap-2 mb-8">
          <div className="w-10 h-10 rounded-xl bg-[#00E676] flex items-center justify-center">
            <TrendingUp className="text-black" size={22} />
          </div>
          <span className="font-display text-2xl font-bold tracking-tight">ProCrypto</span>
        </div>

        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mb-1">
          {mode === "login" ? "Welcome back" : "Create account"}
        </h1>
        <p className="text-sm text-slate-400 mb-8">
          {mode === "login"
            ? "Sign in to trade and manage your USDT."
            : "Get a unique deposit address and start trading."}
        </p>

        <div className="flex gap-2 p-1 bg-[#121721] border border-[#1E293B] rounded-xl mb-6">
          {["login", "register"].map((m) => (
            <button
              key={m}
              data-testid={`auth-tab-${m}`}
              disabled={busy}
              onClick={() => { setMode(m); setError(""); setChallenge(""); setVerificationToken(""); setOtp(""); setNotice(""); }}
              className={`flex-1 py-2 rounded-lg text-sm font-semibold capitalize transition-colors ${
                mode === m ? "bg-[#00E676] text-black" : "text-slate-400"
              }`}
            >
              {m === "login" ? "Sign In" : "Register"}
            </button>
          ))}
        </div>

        {resetSuccess && <p role="status" className="text-sm text-emerald-400 mb-4">{resetSuccess}</p>}
        <form onSubmit={submit} className="space-y-4">
          {mode === "register" && (
            <Input
              data-testid="register-name-input"
              placeholder="Full name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="bg-[#121721] border-[#1E293B] h-12 text-white"
            />
          )}          <Input
            data-testid="login-email-input"
            type="email"
            placeholder="Email"
            value={email}
            readOnly={mode === "register" && !!challenge}
            onChange={(e) => setEmail(e.target.value)}
            required
            className="bg-[#121721] border-[#1E293B] h-12 text-white"
          />
          <Input
            data-testid="login-password-input"
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            className="bg-[#121721] border-[#1E293B] h-12 text-white"
          />

          {mode === "register" && (
            <div>
              <Input
                data-testid="register-referral-input"
                placeholder="Referral code (optional)"
                value={referral}
                onChange={(e) => setReferral(e.target.value.toUpperCase())}
                className="bg-[#121721] border-[#1E293B] h-12 text-white font-mono-num tracking-widest uppercase"
              />
              <p className="text-[11px] text-[#00E676] mt-1.5 flex items-center gap-1">
                <Gift size={12} /> Use a code and get an instant 7 USDT bonus
              </p>
            </div>
          )}

          {error && (
            <p data-testid="auth-error" className="text-sm text-[#FF3B30]">
              {error}
            </p>
          )}

          {mode === "register" && challenge && <div className="space-y-3">
            <p role="status" className="text-xs text-emerald-300">{notice}</p>
            <Input aria-label="Email verification code" inputMode="numeric" autoComplete="one-time-code" pattern="[0-9]{6}" maxLength={6} required value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))} placeholder="6-digit email code" className="bg-[#121721] border-[#1E293B] h-12 text-white" />
            <div className="flex justify-between text-xs">
              <button type="button" disabled={busy || cooldown > 0} onClick={async () => {
                setBusy(true); setError("");
                try { await sendOtp(); } catch (e) { setError(formatApiError(e.response?.data?.detail)); }
                finally { setBusy(false); }
              }} className="text-emerald-400 disabled:text-slate-500">{cooldown ? `Resend in ${cooldown}s` : "Resend code"}</button>
              <button type="button" disabled={busy} onClick={() => { setChallenge(""); setVerificationToken(""); setOtp(""); setNotice(""); }} className="text-slate-300">Change email</button>
            </div>
          </div>}

          <Button
            data-testid="login-submit-button"
            type="submit"
            disabled={busy}
            className="w-full h-12 bg-[#00E676] hover:bg-[#00C853] text-black font-bold text-base rounded-xl"
          >
            {busy ? <Loader2 className="animate-spin" size={20} /> : mode === "login" ? "Sign In" : challenge ? "Verify and create account" : "Send verification code"}
          </Button>
        </form>
        {mode === "login" && <button type="button" onClick={() => { setForgot(true); setError(""); }} className="block mx-auto mt-4 text-sm font-medium text-emerald-400 hover:underline">Forgot password?</button>}
        <div className="mt-5"><AndroidDownload /></div>
        <Link to="/how-to-play" className="block mt-4 text-center text-sm text-emerald-400 underline">How to Play</Link>
      </motion.div>
    </div>
  );
}
